import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Package, Warehouse, PackageCheck, ClipboardCheck, Boxes,
  ArrowRightLeft, FileOutput, History, ScrollText, BarChart3, Users, Settings, LogOut,
  RefreshCw, Scale, ListTree, Building2, FileText, ShoppingCart, ShoppingBag, Store,
  RotateCcw, Database, Wallet, Tag, Palette, Ruler, Barcode, TrendingUp, CheckSquare, ShieldCheck,
  Handshake, Truck, ChevronDown, ChevronRight, Menu, X, Bell, Globe, Percent, Scissors, Bookmark, Printer,
  Factory, Layers, ClipboardList, CalendarClock, Megaphone, BookOpen,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { ROLE_LABELS } from "../lib/roles";

// Rute yang termasuk area Inventory — tampilkan banner peringatan migrasi stok.
const INVENTORY_PATHS = ["/products", "/product-list", "/transfers", "/stock", "/movements", "/warehouses", "/reconciliation"];
const isInventoryRoute = (path) => INVENTORY_PATHS.includes(path) || path.startsWith("/inventory");

// type: 'section' (header), 'link' (single) or 'group' (collapsible with items)
const NAV = [
  { type: "link", to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, key: "dashboard" },

  { type: "section", label: "Sales" },
  {
    type: "group", label: "Sale", icon: ShoppingBag, items: [
      { to: "/sales/create", label: "Cashier", key: "cashier" },
      { to: "/sales", label: "Sale List", key: "sales_list" },
      { to: "/sales/duplicate-numbers", label: "Audit Nomor Kembar", key: "sales_list" },
      { to: "/sales/shipping", label: "Shipping Sale", key: "shipping_sale" },
    ],
  },
  {
    type: "group", label: "Keep", icon: Bookmark, items: [
      { to: "/sales/keep/create", label: "Create Keep", key: "keep" },
      { to: "/sales/keep", label: "List Keep", key: "keep" },
      { to: "/sales/keep/recap", label: "Ringkasan Keep Aktif", key: "keep" },
    ],
  },
  {
    type: "group", label: "Pre-Order", icon: CalendarClock, items: [
      { to: "/sales/preorder/overview", label: "Overview PO", key: "preorder" },
      { to: "/sales/preorder-keep-create", label: "Create Keep PO", key: "preorder" },
      { to: "/sales/preorder-batches", label: "Campaign & Batch", key: "preorder" },
      { to: "/sales/preorder-warehouse", label: "Pesanan PO", key: "preorder" },
      { to: "/sales/preorder/analysis", label: "Analisis Produk & Varian", key: "preorder" },
      { to: "/sales/preorder/production", label: "Kebutuhan Produksi", key: "preorder" },
      { to: "/sales/preorder", label: "Kuota Pre-Order", key: "preorder" },
    ],
  },
  {
    type: "group", label: "Ventedaily", icon: Globe, items: [
      { to: "/ventedaily/online-sales", label: "Online Sales", key: "ventedaily" },
      { to: "/ventedaily/daily-recap", label: "Rangkuman Harian", key: "ventedaily" },
      { to: "/ventedaily/desty-shipped", label: "Desty Integrate", key: "ventedaily" },
      { to: "/ventedaily/pending-shipments", label: "Paket Belum Dikirim", key: "ventedaily" },
      { to: "/ventedaily/order-cancel", label: "Order Cancel", key: "ventedaily" },
      { to: "/ventedaily/shipping", label: "Shipping", key: "ventedaily" },
      { to: "/ventedaily/withdrawal", label: "Withdrawal", key: "ventedaily" },
      { to: "/ventedaily/hermes-docs", label: "Hermes API Docs", key: "ventedaily" },
      { to: "/ventedaily/chatgpt-connector", label: "Konektor ChatGPT", key: "ventedaily", ownerOnly: true },
    ],
  },
  { type: "link", to: "/bazar", label: "Bazar", icon: Store, key: "bazar" },
  { type: "link", to: "/master/customer", label: "Customer", icon: Handshake, key: "customer" },
  { type: "link", to: "/master/discount", label: "Discount", icon: Percent, key: "discount" },

  { type: "section", label: "Inventory" },
  {
    type: "group", label: "Product", icon: Package, items: [
      { to: "/products", label: "Product", key: "product" },
      { to: "/inventory/labels", label: "Product Label", key: "product_label" },
      { to: "/products/variant-mapping", label: "Mapping Varian Marketplace", key: "variant_mapping" },
      { to: "/transfers", label: "Transfer Stock", key: "transfer_stock" },
      { to: "/inventory/stock-in", label: "Stock In", key: "stock_in" },
      { to: "/inventory/stock-out", label: "Stock Out", key: "stock_out" },
      { to: "/inventory/forecast", label: "Stock Prediction", key: "stock_prediction" },
      { to: "/reports/business-intelligence", label: "Business Inteligence", key: "business_intelligence" },
      { to: "/inventory/opname", label: "Stok Opname", key: "opname" },
      { to: "/inventory/defect", label: "Defect", key: "defect" },
      { to: "/inventory/categories", label: "Category", key: "category" },
      { to: "/inventory/colors", label: "Colour", key: "colour" },
      { to: "/inventory/sizes", label: "Size", key: "size" },
    ],
  },
  {
    type: "group", label: "Purchase", icon: ShoppingCart, items: [
      { to: "/purchase-requests", label: "Purchase Request", key: "purchase_request" },
      { to: "/purchase-orders", label: "Purchase Order", key: "purchase_order" },
      { to: "/purchase-po", label: "Purchase PO", key: "preorder" },
    ],
  },
  {
    type: "group", label: "Return", icon: RotateCcw, items: [
      { to: "/return/customer", label: "Return Customer", key: "return_customer" },
      { to: "/return/supplier", label: "Return Supplier", key: "return_supplier" },
    ],
  },
  { type: "link", to: "/suppliers", label: "Supplier", icon: Building2, key: "supplier" },
  { type: "link", to: "/tailor", label: "Tailor", icon: Scissors, key: "tailor" },
  {
    type: "group", label: "Warehouse", icon: Warehouse, items: [
      { to: "/product-list", label: "Product List", key: "product_list" },
      { to: "/stock", label: "Stock Warehouse", key: "stock_warehouse" },
      { to: "/warehouse/monitor", label: "Pantau Stok Gudang", key: "stock_warehouse" },
      { to: "/inventory/asset-report", label: "Laporan Aset Stok", key: "stock_warehouse" },
      { to: "/movements", label: "Stock Movement", key: "stock_movement" },
      { to: "/warehouses", label: "Warehouse & Rack", key: "warehouse_rack" },
    ],
  },

  { type: "section", label: "Production" },
  { type: "link", to: "/production/dashboard", label: "Overview", icon: Factory, key: "prod_overview" },
  { type: "link", to: "/production/kanban", label: "Board Production", icon: Boxes, key: "prod_production" },
  {
    type: "group", label: "Material", icon: Layers, items: [
      { to: "/production/fabrics", label: "Fabrics", key: "prod_material" },
      { to: "/production/accessories", label: "Accessories", key: "prod_material" },
      { to: "/production/material-purchases", label: "Purchase Material", key: "prod_material" },
      { to: "/production/restock-proposals", label: "Usulan Restock", key: "prod_material" },
    ],
  },
  {
    type: "group", label: "Planning", icon: ClipboardList, items: [
      { to: "/production/plans", label: "Production Planning", key: "prod_planning" },
      { to: "/production/bom", label: "BOM", key: "prod_planning" },
      { to: "/production/products", label: "History Product", key: "prod_planning" },
    ],
  },
  {
    type: "group", label: "Production", icon: Boxes, items: [
      { to: "/production/cutting", label: "Cutting", key: "prod_production" },
      { to: "/production/orders", label: "Garment", key: "prod_production" },
      { to: "/production/receiving-hub", label: "Receive Goods", key: "prod_production" },
      { to: "/production/wip", label: "Outstanding WIP", key: "prod_production" },
      { to: "/production/fabric-returns", label: "Sisa Kain", key: "prod_production" },
      { to: "/production/defects", label: "Defect & Rework", key: "prod_production" },
    ],
  },

  { type: "section", label: "Marketing" },
  {
    type: "group", label: "Marketing", icon: Megaphone, items: [
      { to: "/marketing/kol", label: "KOL Management", key: "marketing_kol" },
    ],
  },

  { type: "section", label: "Accounting" },
  {
    type: "group", label: "Finance", icon: Wallet, items: [
      { to: "/finance/cost", label: "Cost", key: "finance" },
      { to: "/finance/expense", label: "Expense", key: "finance" },
      { to: "/finance/settlement", label: "Marketplace Settlement", key: "finance" },
      { to: "/finance/reconciliation-control-center", label: "Reconciliation Control Center", key: "finance" },
      { to: "/finance/bca-reconciliation", label: "Rekonsiliasi BCA", key: "finance" },
      { to: "/finance/shipping-reconciliation", label: "Crosscheck Pengiriman", key: "finance" },
      { to: "/finance/online-report-coverage", label: "Cakupan Laporan Online", key: "finance" },
      { to: "/finance/marketplace-mapping", label: "Mapping Toko", key: "finance" },
      { to: "/finance/rab", label: "RAB & Pengajuan", key: "rab", children: [
        { to: "/finance/rab/pengajuan", label: "Pengajuan Saya", key: "rab" },
        { to: "/finance/rab/review", label: "Review Accounting", key: "rab" },
        { to: "/finance/rab/persetujuan", label: "Persetujuan RAB", key: "rab" },
        { to: "/finance/rab/pendanaan", label: "Pendanaan", key: "rab" },
        { to: "/finance/rab/pembayaran", label: "Pembayaran", key: "rab" },
        { to: "/finance/rab/realisasi", label: "Saldo & Realisasi", key: "rab" },
        { to: "/finance/rab/cost", label: "Cost", key: "rab" },
        { to: "/finance/rab/konfigurasi", label: "Konfigurasi PJ (Owner)", key: "rab", ownerOnly: true },
      ] },
    ],
  },

  { type: "section", label: "Report" },
  { type: "link", to: "/reports", label: "Reports", icon: BarChart3, key: "reports" },
  { type: "link", to: "/production/reports", label: "Production Report", icon: FileText, key: "production_report" },

  { type: "section", label: "User and Role" },
  { type: "link", to: "/users", label: "User & Roles", icon: Users, key: "users" },
  { type: "link", to: "/roles", label: "Custom Role", icon: ShieldCheck, key: "roles" },
  { type: "link", to: "/user-activity", label: "User Activity", icon: History, key: "user_activity" },

  { type: "section", label: "System" },
  { type: "link", to: "/settings", label: "Settings", icon: Settings, key: "settings" },
  { type: "link", to: "/printer", label: "Printer", icon: Printer, key: "printer" },
  { type: "link", to: "/master/customer-group", label: "Customer Group", icon: Users, key: "customer_group" },
  { type: "link", to: "/master/payment-methods", label: "Payment Type", icon: Tag, key: "payment_type" },
  {
    type: "group", label: "POS Integration", icon: RefreshCw, items: [
      { to: "/pos-sync", label: "Synchronize POS", key: "pos_integration" },
      { to: "/import-purchase", label: "Import Purchase POS", key: "pos_integration" },
      { to: "/import-history", label: "History Import", key: "pos_integration" },
      { to: "/reconciliation", label: "Reconciliation POS", key: "pos_integration" },
    ],
  },
  { type: "link", to: "/panduan", label: "Panduan ERP", icon: BookOpen, end: true },
];

// Filter NAV by view permission; drop empty groups and empty sections.
function filterNav(nav, cv) {
  const kept = [];
  for (const item of nav) {
    if (item.type === "link") {
      if (!item.key || cv(item.key)) kept.push(item);
    } else if (item.type === "group") {
      const items = item.items.filter((i) => !i.key || cv(i.key));
      if (items.length) kept.push({ ...item, items });
    } else {
      kept.push(item);
    }
  }
  const res = [];
  for (let i = 0; i < kept.length; i++) {
    if (kept[i].type === "section") {
      if (i + 1 < kept.length && kept[i + 1].type !== "section") res.push(kept[i]);
    } else res.push(kept[i]);
  }
  return res;
}

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function Layout({ children }) {
  const { user, logout, canView } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const filteredNav = filterNav(NAV, canView);

  const initialOpen = () => {
    const st = {};
    NAV.forEach((n) => {
      if (n.type === "group" && n.items.some((i) => i.to === location.pathname)) st[n.label] = true;
    });
    return st;
  };
  const [open, setOpen] = useState(initialOpen);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [brand, setBrand] = useState(() => { try { return JSON.parse(localStorage.getItem("branding") || "null") || {}; } catch { return {}; } });
  const [urgentCount, setUrgentCount] = useState(0);
  const [bellAlerts, setBellAlerts] = useState([]);
  const [bellOpen, setBellOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [notifUnread, setNotifUnread] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const shownPrayerRef = useRef(new Set());
  const loadNotifs = () => api.get("/notifications", { params: { limit: 30 } }).then((r) => {
    const items = r.data.items || [];
    setNotifs(items); setNotifUnread(r.data.unread || 0);
    const nowMs = Date.now();
    items.forEach((n) => {
      if (n.type !== "prayer" || n.read || shownPrayerRef.current.has(n.id)) return;
      const ts = Date.parse(n.created_at);
      if (ts && nowMs - ts < 120000) {
        shownPrayerRef.current.add(n.id);
        toast(n.title, { description: n.body, duration: 20000, closeButton: true });
      }
    });
  }).catch(() => {});
  useEffect(() => {
    if (brand.app_name) document.title = brand.app_name;
    api.get("/settings/branding").then((r) => { setBrand(r.data); localStorage.setItem("branding", JSON.stringify(r.data)); document.title = r.data.app_name || "Ventedaily"; }).catch(() => {});
    api.get("/restock/alerts").then((r) => { setUrgentCount(r.data?.urgent_skus || 0); setBellAlerts(r.data?.bestseller_alerts || []); }).catch(() => {});
    loadNotifs();
    const t = setInterval(loadNotifs, 60000);
    return () => clearInterval(t);
  }, []);
  const openNotif = async (n) => {
    setNotifOpen(false);
    if (!n.read) { await api.post(`/notifications/${n.id}/read`).catch(() => {}); }
    if (n.link) navigate(n.link);
    closeMobile();
    loadNotifs();
  };
  const markAllNotif = async () => { await api.post("/notifications/read-all").catch(() => {}); loadNotifs(); };
  const brandName = brand.app_name || "Ventedaily";
  const toggle = (label) => setOpen((o) => ({ ...o, [label]: !o[label] }));
  const doLogout = () => { logout(); navigate("/login"); };
  const closeMobile = () => setMobileOpen(false);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const linkCls = (isActive) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
      isActive ? "bg-ivory/15 text-ivory" : "text-ivory/70 hover:bg-ivory/10 hover:text-ivory"
    }`;

  return (
    <div className="min-h-screen flex bg-ivory">
      {/* Mobile top bar */}
      <header className="lg:hidden fixed top-0 inset-x-0 h-14 bg-cocoa-dark z-30 flex items-center gap-3 px-4">
        <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn" className="text-ivory p-1 -ml-1">
          <Menu className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-ivory/15 flex items-center justify-center overflow-hidden">
            {brand.logo_url ? <img src={brand.logo_url} alt="" className="w-full h-full object-contain" /> : <Warehouse className="w-4 h-4 text-ivory" />}
          </div>
          <span className="font-heading font-bold text-ivory">{brandName}</span>
        </div>
      </header>

      {/* Overlay */}
      {mobileOpen && <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={closeMobile} data-testid="sidebar-overlay" />}

      <aside className={`w-[280px] shrink-0 bg-cocoa-dark flex flex-col fixed h-screen z-50 transition-transform duration-300 lg:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`} data-testid="sidebar">
        <div className="px-6 py-5 border-b border-ivory/10 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-ivory/15 flex items-center justify-center overflow-hidden">
              {brand.logo_url ? <img src={brand.logo_url} alt="" className="w-full h-full object-contain" /> : <Warehouse className="w-5 h-5 text-ivory" />}
            </div>
            <div>
              <div className="font-heading font-bold text-ivory leading-tight">{brandName}</div>
              <div className="text-[11px] text-ivory/50 -mt-0.5">Warehouse & ERP</div>
            </div>
          </div>
          <button onClick={closeMobile} className="lg:hidden text-ivory/70 hover:text-ivory p-1" data-testid="sidebar-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Notifikasi in-app */}
        <div className="px-3 pt-3">
          <button onClick={() => setNotifOpen((o) => !o)} data-testid="notif-bell"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-ivory/10 hover:bg-ivory/20 text-ivory/90 transition-colors">
            <Bell className="w-4 h-4" />
            <span className="text-sm font-semibold">Notifikasi</span>
            {notifUnread > 0 && <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-400 text-cocoa-dark text-[10px] font-bold" data-testid="notif-count">{notifUnread > 99 ? "99+" : notifUnread}</span>}
          </button>
          {notifOpen && (
            <div className="mt-1.5 max-h-72 overflow-y-auto rounded-lg bg-cocoa border border-ivory/10 divide-y divide-ivory/5" data-testid="notif-list">
              <div className="flex items-center justify-between px-3 py-1.5">
                <span className="text-[11px] text-ivory/50 uppercase tracking-wide">Terbaru</span>
                {notifUnread > 0 && <button onClick={markAllNotif} className="text-[11px] text-ivory/70 hover:text-ivory" data-testid="notif-read-all">Tandai dibaca</button>}
              </div>
              {notifs.map((n) => (
                <button key={n.id} onClick={() => openNotif(n)} data-testid={`notif-item-${n.id}`}
                  className={`w-full text-left px-3 py-2 hover:bg-ivory/5 ${n.read ? "" : "bg-ivory/5"}`}>
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />}
                    <div className="min-w-0">
                      <div className="text-[13px] text-ivory font-medium leading-snug">{n.title}</div>
                      {n.body && <div className="text-[11px] text-ivory/60 mt-0.5 truncate">{n.body}</div>}
                      <div className="text-[10px] text-ivory/40 mt-0.5">{(n.created_at || "").slice(0, 16).replace("T", " ")}</div>
                    </div>
                  </div>
                </button>
              ))}
              {!notifs.length && <div className="px-3 py-4 text-center text-[12px] text-ivory/50" data-testid="notif-empty">Belum ada notifikasi.</div>}
            </div>
          )}
        </div>

        {/* Alert Best Seller Kritis */}
        {bellAlerts.length > 0 && (
          <div className="px-3 pt-3">
            <button onClick={() => setBellOpen((o) => !o)} data-testid="bestseller-alert-bell"
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-200 transition-colors">
              <Bell className="w-4 h-4" />
              <span className="text-sm font-semibold">Best Seller Rawan Habis</span>
              <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold" data-testid="bestseller-alert-count">{bellAlerts.length}</span>
            </button>
            {bellOpen && (
              <div className="mt-1.5 max-h-72 overflow-y-auto rounded-lg bg-cocoa border border-ivory/10 divide-y divide-ivory/5" data-testid="bestseller-alert-list">
                {bellAlerts.map((a) => (
                  <button key={a.id} onClick={() => { setBellOpen(false); navigate("/inventory/forecast"); closeMobile(); }}
                    data-testid={`bestseller-alert-${a.id}`}
                    className="w-full text-left px-3 py-2 hover:bg-ivory/5">
                    <div className="text-[13px] text-ivory font-medium leading-snug">{a.name}</div>
                    <div className="text-[11px] text-ivory/60 flex items-center gap-2 mt-0.5">
                      <span className={a.priority === "Urgent" ? "text-red-300 font-semibold" : "text-amber-300 font-semibold"}>{a.priority === "Urgent" ? "URGENT" : "PREPARE"}</span>
                      <span>Stok {a.available}</span>
                      <span>· Cover {a.days_cover === null ? "—" : `${a.days_cover}h`}</span>
                      <span>· Restock {a.suggested_restock}</span>
                    </div>
                  </button>
                ))}
                <button onClick={() => { setBellOpen(false); navigate("/inventory/forecast"); closeMobile(); }}
                  className="w-full text-center px-3 py-2 text-[12px] text-ivory/70 hover:text-ivory hover:bg-ivory/5" data-testid="bestseller-alert-viewall">
                  Lihat semua di Restock Planning →
                </button>
              </div>
            )}
          </div>
        )}

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
          {filteredNav.map((item, navIdx) => {
            if (item.type === "section") {
              return (
                <div key={`section-${item.label}-${navIdx}`} className="px-3 pt-5 pb-1.5 text-[10px] font-bold tracking-widest text-ivory/40 uppercase">
                  {item.label}
                </div>
              );
            }
            if (item.type === "link") {
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end !== undefined ? item.end : true}
                  data-testid={`nav-${item.to === "/" ? "dashboard" : item.to.slice(1)}`}
                  className={({ isActive }) => linkCls(isActive)}
                >
                  <item.icon className="w-[18px] h-[18px]" />
                  {item.label}
                </NavLink>
              );
            }
            const isOpen = !!open[item.label];
            const groupActive = item.items.some((i) => i.to === location.pathname);
            return (
              <div key={`group-${item.label}-${navIdx}`}>
                <button
                  onClick={() => toggle(item.label)}
                  data-testid={`navgroup-${slug(item.label)}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                    groupActive ? "text-ivory" : "text-ivory/80 hover:bg-ivory/10 hover:text-ivory"
                  }`}
                >
                  <item.icon className="w-[18px] h-[18px]" />
                  <span className="flex-1 text-left">{item.label}</span>
                  {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                {isOpen && (
                  <div className="mt-0.5 mb-1 ml-4 pl-3 border-l border-ivory/15 space-y-0.5">
                    {item.items.filter((sub) => !(sub.ownerOnly && user?.role !== "owner")).map((sub) => (
                      <React.Fragment key={sub.to}>
                      <NavLink
                        to={sub.to}
                        end
                        data-testid={`nav-${sub.to.slice(1).replace(/\//g, "-")}`}
                        className={({ isActive }) =>
                          `block px-3 py-2 rounded-lg text-[13px] transition-colors flex items-center ${
                            isActive ? "bg-ivory/15 text-ivory font-medium" : "text-ivory/60 hover:bg-ivory/10 hover:text-ivory"
                          }`
                        }
                      >
                        {sub.label}
                        {sub.to === "/inventory/forecast" && urgentCount > 0 && (
                          <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold" data-testid="nav-urgent-badge">{urgentCount > 999 ? "999+" : urgentCount}</span>
                        )}
                      </NavLink>
                      {sub.children && (
                        <div className="ml-3 pl-2 border-l border-ivory/10 space-y-0.5">
                          {sub.children.map((c) => (
                            (c.ownerOnly && user?.role !== "owner") ? null :
                            <NavLink key={c.to} to={c.to} end data-testid={`nav-${c.to.slice(1).replace(/\//g, "-")}`}
                              className={({ isActive }) => `block px-3 py-1.5 rounded-lg text-[12px] transition-colors ${isActive ? "bg-ivory/15 text-ivory font-medium" : "text-ivory/50 hover:bg-ivory/10 hover:text-ivory"}`}>
                              {c.label}
                            </NavLink>
                          ))}
                        </div>
                      )}
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="border-t border-ivory/10 p-3">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-9 h-9 rounded-full bg-ivory/15 flex items-center justify-center text-ivory font-heading font-bold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-ivory truncate">{user?.name}</div>
              <div className="text-[11px] text-ivory/50 truncate">{user?.role_name || ROLE_LABELS[user?.role] || user?.role}</div>
            </div>
            <button data-testid="logout-btn" onClick={doLogout} className="text-ivory/60 hover:text-white p-1.5 rounded-md hover:bg-ivory/10">
              <LogOut className="w-[18px] h-[18px]" />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 lg:ml-[280px] min-h-screen pt-14 lg:pt-0">
        <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px]">
          {children}
        </div>
      </main>
    </div>
  );
}
